/**
 * TenantAuthContext — JWT-only tenant authentication.
 *
 * After a tenant logs in via the unified /auth/login endpoint the JWT is
 * stored in localStorage by authTokenService. This context reads the JWT
 * claims, fetches the tenant portal data, and exposes it to the portal UI.
 *
 * Google sign-in uses Google Identity Services → POST /auth/google → app JWT.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { authTokenService } from '../services/auth.service';
import { useToast } from '../components/ui/Toaster';
import { TenantData } from '../services/tenantService';
import { TENANT_SESSION_EXPIRED_EVENT, MESSAGES } from '../services/tenantApiErrors';
import { postGoogleAuth } from '../services/googleAuth.api';
import { navigateAfterGoogleAuth } from '../utils/googleAuthNavigation';

type Tenant = TenantData;

interface TenantAuthContextType {
  tenant: Tenant | null;
  loading: boolean;
  isAuthenticated: boolean;
  signingIn: boolean;
  signInWithGoogle: (credential: string) => Promise<void>;
  syncTenantFromToken: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshTenantData: () => Promise<void>;
}

const TenantAuthContext = createContext<TenantAuthContextType | undefined>(undefined);

export const useTenantAuth = () => {
  const ctx = useContext(TenantAuthContext);
  if (!ctx) throw new Error('useTenantAuth must be used within a TenantAuthProvider');
  return ctx;
};

// ── Provider ─────────────────────────────────────────────────────────────────

export const TenantAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const hasLoadedRef = useRef(false);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const broadcastAuthState = useCallback((data: Tenant | null) => {
    if (data) {
      localStorage.setItem('tenant-auth-state', JSON.stringify({ tenantId: data.id, timestamp: Date.now() }));
    } else {
      localStorage.removeItem('tenant-auth-state');
    }
  }, []);

  /** Fetch full tenant portal data using the app JWT. */
  const fetchPortalData = useCallback(async (tenantProfileId: string): Promise<Tenant | null> => {
    const token = authTokenService.getToken();
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/tenants/portal/${tenantProfileId}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        console.error('❌ TenantAuth: portal API returned', res.status, 'for', tenantProfileId);
        return null;
      }
      return await res.json();
    } catch (err) {
      console.error('❌ TenantAuth: fetchPortalData error', err);
      return null;
    }
  }, []);

  // ── Session-expired listener ────────────────────────────────────────────

  useEffect(() => {
    const handleExpired = () => {
      setTenant(null);
      authTokenService.clearToken();
      broadcastAuthState(null);
      toast.error('Session expired', { description: MESSAGES.SESSION_EXPIRED });
      if (window.location.pathname.startsWith('/tenant')) {
        navigate('/tenant/login');
      }
    };
    window.addEventListener(TENANT_SESSION_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(TENANT_SESSION_EXPIRED_EVENT, handleExpired);
  }, [toast, navigate, broadcastAuthState]);

  // ── Multi-tab sync ──────────────────────────────────────────────────────

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'tenant-auth-state' && e.newValue) {
        try {
          const s = JSON.parse(e.newValue);
          if (s.tenantId && Date.now() - s.timestamp < 5 * 60_000 && s.tenantId !== tenant?.id) {
            refreshTenantData();
          }
        } catch { /* ignore */ }
      }
      // Another tab cleared the JWT → sign this tab out
      if (e.key === 'tp_auth_token' && !e.newValue) {
        setTenant(null);
        broadcastAuthState(null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  // ── Load tenant data when on /tenant/* routes ───────────────────────────
  //
  // Re-runs when location.pathname changes (SPA navigation) or when tenant
  // is cleared (sign-out). On page refresh the first run already has the
  // correct pathname, so both scenarios are covered.

  useEffect(() => {
    const onTenantRoute = location.pathname.startsWith('/tenant');

    if (!onTenantRoute) {
      hasLoadedRef.current = false; // Reset when leaving tenant routes
      setLoading(false);
      return;
    }

    // Already have tenant data — nothing to load
    if (tenant) {
      setLoading(false);
      return;
    }

    // Already attempted a load for this visit to /tenant/*
    if (hasLoadedRef.current) {
      setLoading(false);
      return;
    }

    const claims = authTokenService.getClaims();
    if (!claims || claims.role !== 'TENANT' || !claims.tenantProfileId) {
      hasLoadedRef.current = true;
      setLoading(false);
      return;
    }
    if (claims.mustVerifyPhone) {
      hasLoadedRef.current = true;
      setLoading(false);
      if (location.pathname !== '/tenant/verify-phone') {
        navigate('/tenant/verify-phone', { replace: true });
      }
      return;
    }

    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPortalData(claims.tenantProfileId!);
        if (!cancelled && data) {
          setTenant(data);
          broadcastAuthState(data);
        }
      } catch (err) {
        console.error('❌ TenantAuth: init fetch error', err);
      } finally {
        if (!cancelled) {
          hasLoadedRef.current = true;
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, tenant]);

  // ── Deposit check ───────────────────────────────────────────────────────

  useEffect(() => {
    if (tenant?.depositPaymentStatus === 'PENDING' && tenant.depositPaymentToken) {
      if (!window.location.pathname.startsWith('/deposit-payment')) {
        navigate(`/deposit-payment/${tenant.depositPaymentToken}`);
      }
    }
  }, [tenant, navigate]);

  // ── /tenant/portal → /tenant/portal/:id ─────────────────────────────────

  useEffect(() => {
    if (tenant && !loading && location.pathname === '/tenant/portal') {
      navigate(`/tenant/portal/${tenant.id}`);
    }
  }, [tenant, loading, navigate, location.pathname]);

  // ── Refresh (public) ────────────────────────────────────────────────────

  const refreshTenantData = useCallback(async () => {
    const claims = authTokenService.getClaims();
    if (!claims?.tenantProfileId) return;
    try {
      const data = await fetchPortalData(claims.tenantProfileId);
      if (data) {
        setTenant(data);
        broadcastAuthState(data);
      }
    } catch { /* silent */ }
  }, [fetchPortalData, broadcastAuthState]);

  // ── Sign-in via email/password (JWT path) ───────────────────────────────

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      // Call unified auth endpoint
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json();
      if (!res.ok) {
        const err: any = new Error(body.error || 'Login failed');
        err.code = body.code;
        throw err;
      }

      const claims = authTokenService.setToken(body.token);
      if (!claims) throw new Error('Invalid token received.');
      if (claims.role !== 'TENANT') throw new Error('This account is not a tenant account.');
      if (!claims.tenantProfileId) throw new Error('Tenant profile is not linked. Contact your landlord.');
      if (claims.mustVerifyPhone) {
        toast.success('Phone verification required', { description: 'Enter the code sent to your phone.' });
        navigate('/tenant/verify-phone', { replace: true });
        return;
      }

      // Fetch full portal data
      const portalData = await fetchPortalData(claims.tenantProfileId);
      if (!portalData) throw new Error('Could not load your portal. Please try again.');

      setTenant(portalData);
      broadcastAuthState(portalData);
      toast.success('Welcome back!', { description: 'Redirecting to your portal...' });
      navigate(`/tenant/portal/${portalData.id}`, { replace: true });
    } finally {
      setSigningIn(false);
    }
  }, [signingIn, fetchPortalData, broadcastAuthState, navigate, toast]);

  const syncTenantFromToken = useCallback(async () => {
    const claims = authTokenService.getClaims();
    if (!claims?.tenantProfileId || claims.role !== 'TENANT') return;
    const portalData = await fetchPortalData(claims.tenantProfileId);
    if (portalData) {
      setTenant(portalData);
      broadcastAuthState(portalData);
    }
  }, [fetchPortalData, broadcastAuthState]);

  // ── Google sign-in (GIS credential → JWT → portal) ───────────────────────

  const signInWithGoogle = useCallback(async (credential: string) => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      const result = await postGoogleAuth({ credential, mode: 'login', intent: 'tenant' });
      authTokenService.setToken(result.token);
      await syncTenantFromToken();
      toast.success('Welcome!', { description: 'Redirecting to your portal...' });
      navigateAfterGoogleAuth(result, navigate);
    } catch (err: any) {
      if (err?.code === 'DEPOSIT_PAYMENT_REQUIRED' && err.depositPaymentToken) {
        toast.error('Deposit required', { description: 'Complete your deposit payment first.' });
        navigate(`/deposit-payment/${err.depositPaymentToken}`);
        return;
      }
      throw new Error(err.message || 'Google sign-in failed.');
    } finally {
      setSigningIn(false);
    }
  }, [signingIn, navigate, toast, syncTenantFromToken]);

  // ── Sign-up (first password) ────────────────────────────────────────────

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Registration failed.');

      const claims = authTokenService.setToken(body.token);
      if (!claims || claims.role !== 'TENANT' || !claims.tenantProfileId) {
        throw new Error('Account created but not linked as tenant.');
      }
      const portalData = await fetchPortalData(claims.tenantProfileId);
      if (!portalData) throw new Error('Could not load portal.');

      setTenant(portalData);
      broadcastAuthState(portalData);
      navigate(`/tenant/portal/${portalData.id}`, { replace: true });
    } finally {
      setSigningIn(false);
    }
  }, [signingIn, fetchPortalData, broadcastAuthState, navigate]);

  // ── Sign out ────────────────────────────────────────────────────────────

  const signOut = useCallback(async () => {
    authTokenService.clearToken();
    setTenant(null);
    broadcastAuthState(null);
    // Defensively clear any lingering Firebase session
    try {
      const { signOut: fbSignOut } = await import('firebase/auth');
      const { auth, tenantAuth } = await import('../config/firebase');
      await fbSignOut(tenantAuth).catch(() => {});
      await fbSignOut(auth).catch(() => {});
    } catch { /* firebase not loaded — fine */ }
    navigate('/tenant/login', { replace: true });
  }, [broadcastAuthState, navigate]);

  // ── Context value ───────────────────────────────────────────────────────

  // Synchronous check: covers the render BEFORE the load effect fires.
  // Without this, TenantProtectedRoute would see loading=false + tenant=null
  // and redirect to login before the effect can start fetching.
  const onTenantRoute = location.pathname.startsWith('/tenant');
  const pendingLoad = onTenantRoute && !tenant && !hasLoadedRef.current && (() => {
    try {
      const c = authTokenService.getClaims();
      return !!c && c.role === 'TENANT' && !!c.tenantProfileId;
    } catch { return false; }
  })();

  const value: TenantAuthContextType = {
    tenant,
    loading: loading || !!pendingLoad,
    isAuthenticated: !!tenant,
    signingIn,
    signInWithGoogle,
    syncTenantFromToken,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    refreshTenantData,
  };

  return <TenantAuthContext.Provider value={value}>{children}</TenantAuthContext.Provider>;
};
